import { FolderOpen } from 'lucide-react';
import SolutionPage from '../../components/SolutionPage';

export default function InternalOrganization() {
  return (
    <SolutionPage
      label="Solution"
      headline="When teams rely on memory and informal systems, things fall through the gaps."
      subheadline="Internal disorganization rarely shows up all at once. It accumulates quietly, in missed follow-ups, unclear handoffs, duplicated effort, and the constant low-level stress of not knowing what is happening where."
      heroImage="https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&w=1920"
      icon={FolderOpen}
      problemTitle="Informal coordination works fine, until it does not."
      problemBody={[
        'Many businesses reach a point where informal coordination (spoken agreements, email threads, mental notes) begins to break down. What worked when the team was smaller becomes unreliable as the business grows or as demands increase.',
        'The result is not usually a single dramatic failure. It is a gradual accumulation of small breakdowns: a customer not followed up with, a task assumed complete that was not, an important piece of information lost in an email chain.',
        'The people involved are often doing their best. The issue is that the process, or the absence of one, is not giving them what they need to stay aligned.'
      ]}
      outcomes={[
        {
          title: 'Clearer visibility across ongoing work',
          description:
            'When the status of tasks and client interactions is organized and accessible, the whole team operates with better awareness and less uncertainty.',
        },
        {
          title: 'Reliable handoffs between team members',
          description:
            'Clear processes for passing work between people reduce the risk of things being missed or misunderstood during transitions.',
        },
        {
          title: 'Less time spent searching for information',
          description:
            'When information has a consistent home and is maintained well, staff spend less time asking questions and more time moving work forward.',
        },
        {
          title: 'More consistent client and customer experience',
          description:
            'When internal coordination is reliable, customers receive a consistent experience regardless of which team member they interact with.',
        },
      ]}
      whatWeDoTitle="We design internal workflows that make coordination clear and reliable."
      whatWeDoBody={[
        'We begin by understanding how work actually moves through your business: what the steps are, where the handoffs happen, and where things most commonly break down.',
        'We then design practical workflows suited to your team and the way you work. We avoid unnecessary complexity and focus on solutions that your team will actually use and maintain.',
        'The result is a clearer internal environment where people know what needs doing, who is responsible, and where to find the information they need, without constant meetings or check-ins to stay aligned.'
      ]}
      whatYouGain={[
        'A clearer picture of what is happening across your business at any given time.',
        'Reduced reliance on memory and informal coordination.',
        'Fewer things falling through the gaps during busy or high-demand periods.',
        'A more consistent experience for clients, regardless of who is handling their work.',
        'Less time spent searching for information or following up internally.',
        'A team that can operate with greater independence and confidence.',
      ]}
      relatedSolutions={[
        { title: 'Business Workflow Support', href: '/solutions/business-workflow' },
        { title: 'Appointment Coordination', href: '/solutions/appointment-coordination' },
        { title: 'Customer Communication', href: '/solutions/customer-communication' },
      ]}
    />
  );
}
